import { useState } from "react";
import { Badge, EditRowActions, Input, Switch, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { TipoDocumentoAdicional } from "@/lib/types";
import { useEditarTipoDocumento, useEliminarTipoDocumento } from "../hooks/use-tipo-documento-mutations";

/**
 * Fila del catálogo de tipos de documento · alterna vista/edición inline
 * (nombre + flag activo). Renombrar/eliminar NO afecta documentos ya subidos
 * (guardan el nombre como snapshot); desactivar saca el tipo del picker.
 */
export function TipoDocumentoRow({ tipo }: { tipo: TipoDocumentoAdicional }) {
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(tipo.nombre);
  const [activo, setActivo] = useState(tipo.activo);
  const confirm = useConfirmDialog();

  const guardar = useEditarTipoDocumento({ onEditado: () => setEditando(false) });
  const eliminar = useEliminarTipoDocumento();

  const cancelar = () => {
    setNombre(tipo.nombre);
    setActivo(tipo.activo);
    setEditando(false);
  };

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Eliminar tipo de documento",
      message: (
        <>
          ¿Eliminar el tipo <b>{tipo.nombre}</b>? Dejará de aparecer en el picker del paso
          Documentos. Los documentos ya subidos con este tipo NO cambian (guardan el nombre
          como snapshot).
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(tipo.id);
  };

  if (!editando) {
    return (
      <Tr>
        <Td>
          <Text size="sm" fw={500}>
            {tipo.nombre}
          </Text>
        </Td>
        <Td>
          <Badge size="sm" color={tipo.activo ? "exito" : "slate"}>
            {tipo.activo ? "Activo" : "Inactivo"}
          </Badge>
        </Td>
        <Td align="right">
          <EditRowActions mode="view" onEdit={() => setEditando(true)} onDelete={confirmarEliminar} deleting={eliminar.isPending} />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr className="bg-muted/50">
      <Td>
        <Input size="xs" value={nombre} onChange={(e) => setNombre(e.currentTarget.value.toUpperCase())} autoFocus />
      </Td>
      <Td>
        <Switch checked={activo} onCheckedChange={setActivo} label={activo ? "Activo" : "Inactivo"} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="edit"
          saving={guardar.isPending}
          onCancel={cancelar}
          onSave={() => guardar.mutate({ id: tipo.id, input: { nombre: nombre.trim(), activo } })}
        />
      </Td>
    </Tr>
  );
}
